import { FieldValues } from 'react-hook-form'
import './FormInput.css'
import { get, isEmpty, noop } from 'lodash'
import { SvgClearCross, SvgInfo } from '../../assets/images'
import Tooltip from '../Tooltip'
import { FormInputPropTypes, NAMESPACE } from '../../utils'
import { memo, useState } from 'react'

const formatDateWithTimezone = (date: string | Date): string => {
  if (!date) return ''
  const d = new Date(date)
  return d.toISOString() // ISO 8601 format with timezone: "2025-01-22T14:30:00.000Z"
}

const formatDateForInput = (date: string | Date): string => {
  if (!date) return ''
  const d = new Date(date)
  const timezoneOffset = d.getTimezoneOffset()
  return new Date(d.getTime() - timezoneOffset * 60000)
    .toISOString()
    .split('.')[0] // Returns "yyyy-mm-dd" format
}

const formatTimeWithTimezone = (time: string): string => {
  if (!time) return ''
  const currentDateString = new Date().toISOString().split('T')[0] // Get today's date (YYYY-MM-DD)
  const fullDateTime = `${currentDateString}T${time}` // Combining current date with the received time
  const date = new Date(fullDateTime) // Create a Date object
  return date.toISOString().split('T')[1] // ISO 8601 format with timezone: "14:30:00.000Z"
}

const formatTimeForInput = (time: string): string => {
  if (!time) return ''
  const currentDateString = new Date().toISOString().split('T')[0] // Get today's date (YYYY-MM-DD)
  const fullDateTime = `${currentDateString}T${time}` // Combining current date with the received time
  const date = new Date(fullDateTime) // Create a Date object
  const timezoneOffset = date.getTimezoneOffset()
  return new Date(date.getTime() - timezoneOffset * 60000)
    .toISOString()
    .split('.')[0]
    .split('T')[1] // Returns "MM:HH:SS" format
}

const FormInput = <T extends FieldValues>({
  name,
  label,
  type = 'text',
  disabled = false,
  placeholder = '',
  defaultValue,
  register,
  errors,
  isIcon,
  icon,
  iconClick,
  validation,
  className = '',
  color,
  info,
  formatter,
  staticText,
  dataQaId,
  onKeyDown,
  hasPermission = true,
  controlType,
  customStyles = {},
  minHeight = '9',
  isCrossable = false,
  watch,
  onCrossCick,
  handleDrop,
  showStarOnRequired = true,
  rootStyles = '',
  id = `${name}-input`,
  isLabelTooltipVisible = false,
  labelTooltipText,
  inputBorderClass = 'border border-solid group-focus-within:border-secondary-500 group-focus-within:border',
  showTimezone = false,
  timezone = '',
  hideTimeIcon = false,
  inputPaddingClass = 'px-4 py-2',
  isHelperTextVisible,
  showErrors = true,
  ...rest
}: FormInputPropTypes<T>) => {
  const error = get(errors, name)
  const isDateType = type === 'datetime-local'
  const isTimeType = type === 'time' && !showTimezone

  // If defaultValue is a date-time or time string with timezone, format it as needed
  const getValue = () => {
    if (isDateType && defaultValue && typeof defaultValue === 'string')
      return formatDateForInput(defaultValue)
    if (isTimeType && defaultValue && typeof defaultValue === 'string')
      return formatTimeForInput(defaultValue)
    else return defaultValue
  }
  const [value, setValue] = useState(getValue())

  const formattedDefaultValue = getValue()
  const getLabelTooltip = () =>
    isLabelTooltipVisible ? labelTooltipText || label : undefined
  const getFormatter = () => {
    if (formatter) return formatter
    if (type === 'number') {
      return (value: string | number) => parseFloat(value?.toString())
    }
    if (isDateType)
      return (value: string | Date) => {
        setValue(formatDateForInput(value))
        // For date type, we store it with timezone
        return formatDateWithTimezone(value)
      }
    if (isTimeType)
      return (value: string) => {
        setValue(formatTimeForInput(value))
        // For time type, we store it with timezone
        return formatTimeWithTimezone(value)
      }
    return (value: string) => (typeof value === 'string' ? value.trim() : value)
  }

  // Register the field with react-hook-form
  const registerOptions = disabled
    ? {
        setValueAs: getFormatter(),
      }
    : {
        ...validation,
        setValueAs: getFormatter(),
      }

  return (
    <div
      data-qa-id={dataQaId ?? name}
      className={`w-full font-primary ${rootStyles}`}
    >
      <div
        className={`custom-input-main-container group font-extraLight w-full inline-flex relative min-w-0 p-0 m-0 border-0 align-top font-primary min-h-${minHeight}`}
      >
        <div
          className={`custom-input-label flex text-base p-0 origin-top-left truncate max-w-[calc(100%-1.75rem)] absolute left-0 top-0 z-[1] ${
            isLabelTooltipVisible
              ? 'pointer-events-auto'
              : 'pointer-events-none'
          } ${disabled ? 'text-accent-300' : 'text-black'}`}
        >
          {label ? (
            <label
              htmlFor={id}
              className={`font-extralight ${
                isLabelTooltipVisible ? 'pointer-events-auto' : ''
              } block text-xs leading-1 truncate max-w-[calc(100% - 1.5rem)]
              group-focus-within:max-w-[calc(100% - 1.5rem)]  ${
                disabled
                  ? 'text-accent-300'
                  : 'group-focus-within:text-secondary-500 group-hover:text-secondary-500'
              }`}
              style={{ ...customStyles }}
              title={getLabelTooltip() || ''}
            >
              {label}
            </label>
          ) : null}

          {validation?.required || rest?.required ? (
            <span
              className={`ml-1 text-xs leading-1  ${
                disabled ? 'text-accent-300' : 'text-error-500'
              }`}
              style={{ ...customStyles }}
            >
              {showStarOnRequired ? '*' : ''}
            </span>
          ) : null}
        </div>
        <div className="custom-input-container filled-inp w-full box-border relative cursor-text inline-flex items-center rounded">
          <input
            id={id}
            onKeyDown={onKeyDown || noop}
            placeholder={placeholder}
            spellCheck={false}
            type={hasPermission ? type : 'text'}
            className={`custom-input-main max-h-[40px] outline-none font-light text-current border-none box-content bg-transparent m-0 block min-w-0 w-full ${inputPaddingClass} rounded text-base sm:text-xs disabled:bg-accent-50 disabled:text-accent-500 ${className} ${
              isEmpty(placeholder) ? '' : 'custom-input-main-placeholder'
            } ${disabled ? 'cursor-not-allowed' : ''} ${
              hideTimeIcon
                ? '[&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:z-10'
                : ''
            }`}
            disabled={disabled}
            defaultValue={formattedDefaultValue}
            data-qa-id={dataQaId ?? name ?? ''}
            {...(hasPermission && register
              ? register(name, registerOptions)
              : { name })}
            {...(!hasPermission ? { value: NAMESPACE.REDACTED } : {})}
            {...(isDateType || isTimeType ? { value: value } : {})}
            {...rest}
            step={type === 'number' ? 'any' : controlType ? 'any' : 1}
            style={{ ...customStyles }}
            onDrop={handleDrop}
          />
          {isCrossable && !disabled && watch && watch(name) && (
            <button className="cursor-pointer p-2" onClick={onCrossCick}>
              <SvgClearCross />
            </button>
          )}
          {staticText && (
            <div className="flex items-center whitespace-nowrap">
              <span className="ml-2 mr-3 text-gray-400 text-xs">
                {staticText}
              </span>
            </div>
          )}
          {showTimezone && timezone && (
            <div className="flex items-center whitespace-nowrap max-h-[40px] bg-transparent disabled:bg-accent-50 disabled:text-accent-500 absolute right-[6px]">
              <span className="ml-2 mr-3 text-secondary-500 text-xs">
                {timezone}
              </span>
            </div>
          )}

          <div className="flex ml-auto gap-2 items-center">
            {color && (
              <div
                className="h-5 w-5 border rounded-md"
                style={{ backgroundColor: color }}
              ></div>
            )}
            {info && (
              <div
                className={`mr-2.5 ${
                  disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
                onClick={noop}
              >
                <Tooltip
                  content={info}
                  className="text-wrap w-fit"
                  position="bottom"
                >
                  <SvgInfo />
                </Tooltip>
              </div>
            )}
            {isIcon && (
              <div className="cursor-pointer p-2" onClick={iconClick}>
                {icon}
              </div>
            )}
          </div>

          <fieldset
            aria-hidden="true"
            className={`text-left absolute bottom-0 ${
              label ? 'top-[-0.313rem]' : 'top-0'
            } left-0 right-0 m-0 px-2 pointer-events-none rounded ${inputBorderClass} overflow-hidden min-w-0 border-border-150  ${
              disabled
                ? 'border-border-200 '
                : 'group-hover:border-secondary-500'
            } ${!isEmpty(error) ? 'border-error-500' : ''}`}
          >
            {label ? (
              <legend className="custom-input-legend invisible p-0 block w-auto overflow-hidden max-w-0 whitespace-nowrap box-border h-3 group-focus-within:max-w-full group-focus-within:p-1">
                <span
                  className="l-span font-primary text-xs"
                  style={{ ...customStyles }}
                >
                  {label}
                </span>
                {validation?.required ? (
                  <span
                    className={`ml-1 text-xs leading-1 ${
                      disabled ? 'text-accent-300' : 'text-error-500'
                    }`}
                    style={{ ...customStyles }}
                  >
                    {showStarOnRequired ? '*' : ''}
                  </span>
                ) : null}
              </legend>
            ) : null}
          </fieldset>
        </div>
      </div>
      <div className="max-w-[calc(100% - 1.5rem)]">
        {isHelperTextVisible ? (
          <div
            className="ck-content break-words"
            dangerouslySetInnerHTML={{ __html: isHelperTextVisible }}
          ></div>
        ) : null}
        {showErrors && error?.type === 'required' && !error?.message && (
          <span
            className="text-error-500 text-xxs font-primary font-normal"
            style={{ ...customStyles }}
          >
            {NAMESPACE?.REQUIRED_FIELD}
          </span>
        )}
        {showErrors && error?.message && (
          <span
            className="text-error-500 text-xxs font-primary font-normal w-full text-wrap inline-block leading-[1.3]"
            style={{ ...customStyles }}
          >
            {typeof error.message === 'string'
              ? error.message
              : JSON.stringify(error.message)}
          </span>
        )}
      </div>
    </div>
  )
}

const MemoizedFormInput = memo(FormInput) as typeof FormInput

export default MemoizedFormInput
